import { formatLocaleBookingPeriodShort } from "@/lib/dates";
import { AvailabilityCheckingScreen } from "@/components/AvailabilityCheckingScreen";
import { Notice } from "@/components/Notice";
import { CalendarIcon } from "@/components/ui/Icons";
import { useLocale } from "@/i18n";
import type { AlternativePeriod, AlternativePeriodsResponse } from "@/types";
import type { BookingMessages } from "@/i18n";

function endpointShiftLabel(
  dayShift: number,
  endpoint: "arrival" | "departure",
  m: BookingMessages,
): string {
  if (dayShift === 0) {
    return endpoint === "arrival" ? m.availability.sameArrival : m.availability.sameDeparture;
  }
  const days = Math.abs(dayShift);
  const unit = days === 1 ? m.common.day : m.common.days;
  const endpointLabel = endpoint === "arrival" ? m.common.arrival : m.common.departure;
  const shiftLabel =
    dayShift > 0
      ? m.availability.arrivalLater(days, unit)
      : m.availability.arrivalEarlier(days, unit);
  return shiftLabel.replace(m.common.arrival, endpointLabel);
}

function alternativePeriodDetailLabel(
  alt: AlternativePeriod,
  requestedNights: number,
  m: BookingMessages,
): string {
  const departureShift = alt.departure_shift ?? alt.day_shift;
  const parts: string[] = [];
  if (alt.day_shift !== 0 && alt.day_shift === departureShift) {
    const days = Math.abs(alt.day_shift);
    const unit = days === 1 ? m.common.day : m.common.days;
    parts.push(
      alt.day_shift > 0
        ? m.availability.periodLater(days, unit)
        : m.availability.periodEarlier(days, unit),
    );
  } else {
    if (alt.day_shift !== 0) {
      parts.push(endpointShiftLabel(alt.day_shift, "arrival", m));
    }
    if (departureShift !== 0) {
      parts.push(endpointShiftLabel(departureShift, "departure", m));
    }
  }
  if (alt.nights !== requestedNights) {
    parts.push(m.availability.nightsWanted(alt.nights, requestedNights));
  }
  return parts.length > 0 ? parts.join(" · ") : m.availability.samePeriod;
}

export interface AlternativePeriodSuggestionsProps {
  show: boolean;
  loading: boolean;
  alternatives: AlternativePeriodsResponse | null;
  error: string | null;
  categoryLabel?: string;
  onSelect: (alt: AlternativePeriod) => void;
}

export function AlternativePeriodSuggestions({
  show,
  loading,
  alternatives,
  error,
  categoryLabel,
  onSelect,
}: AlternativePeriodSuggestionsProps) {
  const { messages: m, locale } = useLocale();

  if (!show) return null;

  const placesSuffix = categoryLabel ? m.availability.inCategory : "";

  if (loading) {
    return <AvailabilityCheckingScreen variant="alternatives" compact fullscreenOnMobile />;
  }

  if (error) {
    return <Notice variant="error">{error}</Notice>;
  }

  if (alternatives && alternatives.alternatives.length === 0) {
    return (
      <Notice variant="neutral" title={m.availability.noAlternativesTitle(categoryLabel)}>
        <p>
          {m.availability.noAlternativesBody(categoryLabel)} {m.availability.choosePeriod}
        </p>
      </Notice>
    );
  }

  if (!alternatives?.alternatives.length) return null;

  const nightLabel =
    alternatives.nights === 1 ? m.common.night : m.common.nights;

  return (
    <section className="bk-section">
      <div>
        <h3 className="bk-section-title">{m.availability.alternativesTitle(categoryLabel)}</h3>
        <p className="bk-section-hint">
          {m.availability.alternativesHint(alternatives.nights, nightLabel)}
        </p>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {alternatives.alternatives.map((alt) => {
          const altNightLabel = alt.nights === 1 ? m.common.night : m.common.nights;
          return (
            <li key={`${alt.arrival}-${alt.departure}`}>
              <button
                type="button"
                onClick={() => onSelect(alt)}
                className="bk-tile group h-full"
              >
                <span className="bk-tile__icon">
                  <CalendarIcon className="h-5 w-5" />
                </span>
                <span className="bk-tile__body">
                  <span className="bk-tile__title">
                    {formatLocaleBookingPeriodShort(
                      alt.arrival,
                      alt.departure,
                      alt.nights,
                      locale,
                      altNightLabel,
                    )}
                  </span>
                  <span className="bk-tile__hint">
                    {alternativePeriodDetailLabel(alt, alternatives.nights, m)}
                  </span>
                  <span className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                      {alt.available_count}{" "}
                      {alt.available_count === 1
                        ? m.availability.placeFree
                        : m.availability.placesFree}
                      {placesSuffix}
                    </span>
                    <span className="text-xs font-semibold text-brand-700 group-hover:text-brand-800">
                      {m.availability.selectPeriod} →
                    </span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
